import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Package, Clock, User, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const STAGE_COLORS = {
  'Gate Entry Inward': 'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Greige': 'bg-amber-100 text-amber-800 border-amber-300',
  'Greige Storage': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  'Processing': 'bg-sky-100 text-sky-800 border-sky-300',
  'FOB': 'bg-indigo-100 text-indigo-800 border-indigo-300',
  'Section': 'bg-purple-100 text-purple-800 border-purple-300',
  'Assortment': 'bg-pink-100 text-pink-800 border-pink-300',
  'Goods Transfer': 'bg-cyan-100 text-cyan-800 border-cyan-300',
  'Internal Transfer': 'bg-orange-100 text-orange-800 border-orange-300',
  'Gate Entry Outward': 'bg-red-100 text-red-800 border-red-300',
  'Dispatch': 'bg-teal-100 text-teal-800 border-teal-300',
  'Pieces Processing': 'bg-violet-100 text-violet-800 border-violet-300',
  'Label': 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300',
  'SFG': 'bg-blue-100 text-blue-800 border-blue-300',
  'FG': 'bg-lime-100 text-lime-800 border-lime-300',
  'Packing': 'bg-rose-100 text-rose-800 border-rose-300',
};

function getStageColor(section) {
  for (const [key, cls] of Object.entries(STAGE_COLORS)) {
    if (section?.toLowerCase().includes(key.toLowerCase()) ||
        key.toLowerCase().includes(section?.toLowerCase())) {
      return cls;
    }
  }
  return 'bg-gray-100 text-gray-800 border-gray-300';
}

function formatTimestamp(ts) {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  } catch {
    return ts;
  }
}

const USNTracker = () => {
  const navigate = useNavigate();
  const [searchCode, setSearchCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showUSNDetails, setShowUSNDetails] = useState(false);

  const handleTrace = async () => {
    const code = searchCode.trim();
    if (!code) { toast.error('Please enter a USN or Bundle code'); return; }
    setLoading(true);
    setResult(null);
    try {
      const response = await apiClient.get(`/tracker/trace?code=${encodeURIComponent(code)}`);
      setResult(response.data);
      if (response.data.total_events === 0) {
        toast.info('No lifecycle events found for this code');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to trace code';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleTrace();
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="usn-tracker-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10 px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold">USN / Bundle Tracker</h1>
            <p className="text-xs text-muted-foreground">Trace lifecycle from Gate Entry Inward to Outward</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 space-y-4">
        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="flex gap-2">
              <Input
                placeholder="Enter USN code or Bundle code (e.g. BUN041882/25-26)"
                value={searchCode}
                onChange={(e) => setSearchCode(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1"
                data-testid="tracker-search-input"
              />
              <Button onClick={handleTrace} disabled={loading} data-testid="tracker-search-btn">
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Tracing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2"><Search className="w-4 h-4" /> Trace</span>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {result && (
          <>
            {/* Summary */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium">Input:</span>
                    <Badge variant="outline">{result.input_code}</Badge>
                    <Badge className="bg-blue-100 text-blue-800">{result.input_type === 'bundle' ? 'Bundle' : 'USN'}</Badge>
                  </div>
                  {result.bundle_code && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Bundle:</span>
                      <span className="text-sm font-mono font-semibold">{result.bundle_code}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">USN Count:</span>
                    <span className="text-sm font-semibold">{result.usn_count}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Events:</span>
                    <span className="text-sm font-semibold">{result.total_events}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* USN Details (if bundle input) */}
            {result.usn_details?.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <button
                    onClick={() => setShowUSNDetails(!showUSNDetails)}
                    className="flex items-center gap-2 w-full text-left"
                    data-testid="toggle-usn-details"
                  >
                    <FileText className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-semibold">USNs in Bundle ({result.usn_details.length})</span>
                    {showUSNDetails ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
                  </button>
                  {showUSNDetails && (
                    <div className="mt-3 overflow-x-auto">
                      <table className="w-full text-xs" data-testid="usn-details-table">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="px-3 py-2 text-left font-semibold">#</th>
                            <th className="px-3 py-2 text-left font-semibold">USN Code</th>
                            <th className="px-3 py-2 text-left font-semibold">Barcode</th>
                            <th className="px-3 py-2 text-left font-semibold">Design Name</th>
                            <th className="px-3 py-2 text-left font-semibold">Shade</th>
                            <th className="px-3 py-2 text-left font-semibold">Size</th>
                            <th className="px-3 py-2 text-left font-semibold">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.usn_details.map((u, i) => (
                            <tr key={u.usn_code} className="border-b hover:bg-gray-50">
                              <td className="px-3 py-2">{i + 1}</td>
                              <td className="px-3 py-2 font-mono font-medium">{u.usn_code}</td>
                              <td className="px-3 py-2 font-mono">{u.barcode || '—'}</td>
                              <td className="px-3 py-2">{u.design_name || '—'}</td>
                              <td className="px-3 py-2">{u.shade || '—'}</td>
                              <td className="px-3 py-2">{u.size || '—'}</td>
                              <td className="px-3 py-2">{u.description || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Timeline */}
            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  Lifecycle Timeline ({result.total_events} events)
                </h3>
                {result.timeline.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No lifecycle events found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="timeline-table">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-3 py-2 text-left font-semibold w-8">#</th>
                          <th className="px-3 py-2 text-left font-semibold">Stage</th>
                          <th className="px-3 py-2 text-left font-semibold">Section</th>
                          <th className="px-3 py-2 text-left font-semibold">Reference</th>
                          <th className="px-3 py-2 text-left font-semibold">Staff</th>
                          <th className="px-3 py-2 text-left font-semibold">Timestamp</th>
                          <th className="px-3 py-2 text-left font-semibold">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.timeline.map((event, i) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                            <td className="px-3 py-2">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${getStageColor(event.section || event.stage)}`}>
                                {event.stage}
                              </span>
                            </td>
                            <td className="px-3 py-2">{event.section || '—'}</td>
                            <td className="px-3 py-2 font-mono">{event.reference || '—'}</td>
                            <td className="px-3 py-2">
                              {event.staff ? (
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3 text-muted-foreground" />
                                  {event.staff}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{formatTimestamp(event.timestamp)}</td>
                            <td className="px-3 py-2 max-w-xs truncate" title={event.details}>{event.details || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default USNTracker;
